import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';

type ViewOption = {
  name: string;
  tooltip?: string;
};

export type MenuProps = {
  setSelectedView: (name: string) => void;
  selectedView: string;
  viewOptions: string[] | ViewOption[];
  handleViewChange: (name: string) => void;
  defaultTitle: string;
};

export const Menu = ({ setSelectedView, viewOptions, handleViewChange, selectedView, defaultTitle }: MenuProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleMenuSelectionChange = (_event: SyntheticEvent<Element, Event>, value: string | ViewOption | null) => {
    if (value) {
      if (typeof value === 'object') {
        setSelectedView(value.name);
        handleViewChange(value.name);
      } else {
        setSelectedView(value);
        handleViewChange(value);
      }
    } else {
      setSelectedView('');
      handleViewChange('');
    }
  };

  useEffect(() => {
    setSearchQuery(selectedView);
  }, [selectedView]);

  return (
    <>
      <Autocomplete
        inputValue={searchQuery}
        onInputChange={(_e, val: string) => setSearchQuery(val)}
        disableClearable
        onChange={handleMenuSelectionChange}
        options={viewOptions as (string | ViewOption)[]}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        value={selectedView || ''}
        sx={{ padding: 1, color: 'rgb(107 114 128)', fontSize: '.8rem', width: '11rem' }}
        renderInput={(params) => (
          <TextField
            sx={{ width: '100%', fontSize: '.8rem' }}
            {...params}
            variant="standard"
            placeholder={defaultTitle}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                disableUnderline: true,
                style: {
                  fontSize: '.8rem',
                  color: 'black',
                  fontWeight: 500
                }
              }
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const optionTitle = typeof option === 'object' && option.tooltip ? option.tooltip : undefined;
          const optionElement = (
            <Box
              component="li"
              {...props}
              key={typeof option === 'string' ? option : option.name}
              sx={{
                backgroundColor: selected ? 'rgba(0, 0, 255, 0.1)' : 'inherit',
                fontWeight: selected ? 'bold' : 'normal'
              }}>
              {typeof option === 'string' ? option : option.name}
            </Box>
          );
          if (!optionTitle) return optionElement;

          return (
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{optionTitle}</span>}
              placement="right"
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 'none',
                    width: 'auto'
                  }
                }
              }}>
              {optionElement}
            </Tooltip>
          );
        }}
      />
    </>
  );
};
